import type { LectureSource } from '@/schemas/courses'

/**
 * The URL as the browser would resolve it, or `null` for anything that is not
 * an absolute http(s) URL — a blank, a relative path, a pasted `<iframe>`
 * snippet. The normalised `href`, not the raw string: a space in a path is
 * percent-encoded rather than handed to an `src` as sent.
 */
export const httpUrlOrNull = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim() ?? ''
  if (trimmed === '') return null
  try {
    const url = new URL(trimmed)
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null
  } catch {
    return null
  }
}

/**
 * An embed URL the player may frame, or `null`. Never one on the app's own
 * origin: the frame's sandbox grants `allow-scripts allow-same-origin`, which
 * is only safe cross-origin — same-origin, a framed page can lift its own
 * sandbox and read this one, the session's tokens included.
 */
const embeddableUrlOrNull = (value: string | null): string | null => {
  const url = httpUrlOrNull(value)
  return url === null || new URL(url).origin === window.location.origin ? null : url
}

/** An HLS playlist — only Safari's `<video>` plays one without a library. */
const isHlsPlaylist = (url: string): boolean =>
  new URL(url).pathname.toLowerCase().endsWith('.m3u8')

/**
 * Which of a lecture's three video fields a player uses. mm-be
 * (`services/courses/mappers.py`) sends all three for a playable lecture and
 * none for a locked one:
 *
 * - `embed_url` — a hosted player PAGE (legacy's free lectures were Vimeo
 *   player links). FIRST: the host does adaptive streaming, captions and its
 *   own controls in every browser, and an `<iframe>` is all it needs.
 * - `video` — a file path on mm-be's asset host, joined into an absolute URL.
 *   A plain file any `<video>` plays.
 * - `video_url` — a stream URL (mm-be seeds it as an `.m3u8` HLS playlist).
 *   LAST: outside Safari it needs an HLS library the app does not ship.
 *
 * `null` when none of the three is a usable http(s) URL. An embed on the
 * app's own origin is not usable (`embeddableUrlOrNull`); the file fields
 * are tried instead.
 */
export const resolveLectureSource = (wire: {
  embedUrl: string | null
  video: string | null
  videoUrl: string | null
}): LectureSource | null => {
  const embed = embeddableUrlOrNull(wire.embedUrl)
  if (embed !== null) return { kind: 'embed', url: embed }
  const file = httpUrlOrNull(wire.video) ?? httpUrlOrNull(wire.videoUrl)
  if (file !== null) return { kind: 'video', url: file, hls: isHlsPlaylist(file) }
  return null
}
